import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownPassThroughOptions } from "primereact/dropdown";
import { STATE_ABBREV_TO_STATE_NAME } from "../../../datasets";
import { SIC_CODES } from "./sic";
import { CONSTRUCTION_EDIT_OPTIONS } from "./construction";
import { InputTextarea } from "primereact/inputtextarea";
import { ColumnEditorOptions } from "primereact/column";

// This interface allows us to accept ColumnEditorOptions or ValueCallbackOptions...
interface ValueCallbackOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorCallback?: (value: any) => void;
}

export type Editor = React.ComponentType<{
  options: ColumnEditorOptions | ValueCallbackOptions;
  isUnitedStates: boolean;
  disabled: boolean;
  additionalStyles: React.CSSProperties;
  fieldType?: "string" | "number";
}>;

export const OneLineStringEditor: Editor = ({
  options: { value, editorCallback },
  disabled,
  additionalStyles,
}) => {
  return (
    <InputText
      style={{
        width: "calc(100% - 0.5rem)",
        ...additionalStyles,
      }}
      value={value ?? ""}
      onChange={(e) => editorCallback?.(e.target.value)}
      disabled={disabled}
    />
  );
};

export const MultiLineStringEditor: Editor = ({
  options: { value, editorCallback },
  disabled,
  additionalStyles,
}) => {
  return (
    <InputTextarea
      style={{ width: "calc(100% - 0.5rem)", ...additionalStyles }}
      value={value ?? ""}
      onChange={(e) => editorCallback?.(e.target.value)}
      disabled={disabled}
    />
  );
};

export const MoneyEditor: Editor = ({
  options: { value, editorCallback },
  disabled,
  additionalStyles,
  fieldType,
}) => {
  return (
    <InputNumber
      style={{ width: "calc(100% - 0.5rem)" }}
      value={value ?? 0}
      onValueChange={(e) =>
        fieldType === "string"
          ? editorCallback?.(e.value?.toString())
          : editorCallback?.(e.value)
      }
      pt={{ input: { root: { style: additionalStyles } } }}
      mode="currency"
      currency="USD"
      locale="en-US"
      disabled={disabled}
    />
  );
};

export const EnumEditor = ({
  options: { value, editorCallback },
  enumValues,
  disabled,
  additionalStyles,
  pt,
  placeholder,
  loading,
  scrollHeight,
}: {
  // eslint-disable-next-line
  options: { value: any; editorCallback?: (value: any) => void };
  enumValues: (string | undefined)[];
  disabled: boolean;
  additionalStyles: React.CSSProperties;
  pt?: DropdownPassThroughOptions;
  placeholder?: string;
  loading?: boolean;
  scrollHeight?: string;
}) => {
  return (
    <Dropdown
      loading={loading}
      style={{
        width: "calc(100% - 0.5rem)",
        position: "inherit",
        ...additionalStyles,
      }}
      options={enumValues}
      value={value}
      placeholder={placeholder}
      onChange={(e) => editorCallback?.(e.value)}
      disabled={disabled}
      pt={pt}
      filter={enumValues.length > 10}
      scrollHeight={scrollHeight}
    />
  );
};

export const StateEditor: Editor = ({
  options,
  isUnitedStates,
  disabled,
  additionalStyles,
}) => {
  if (isUnitedStates) {
    return (
      <EnumEditor
        options={options}
        enumValues={Object.keys(STATE_ABBREV_TO_STATE_NAME)}
        disabled={disabled}
        additionalStyles={additionalStyles}
      />
    );
  } else {
    return (
      <OneLineStringEditor
        options={options}
        isUnitedStates={isUnitedStates}
        disabled={disabled}
        additionalStyles={additionalStyles}
      />
    );
  }
};

const makeEnumEditor =
  (options: string[]): Editor =>
  (props) => <EnumEditor {...props} enumValues={options} />;

export const YesNoEditor: Editor = makeEnumEditor(["Yes", "No"]);

export const CentralStationEditor: Editor = makeEnumEditor([
  "None",
  "Fire",
  "Burglary",
  "Fire and Burglary",
]);

const ISO_CONSTRUCTION_OPTIONS = CONSTRUCTION_EDIT_OPTIONS;

const SIC_OPTIONS = Object.entries(SIC_CODES).map(([code, desc]) => ({
  label: `${code} (${desc})`,
  value: code,
}));

export const SicCodeEditor: Editor = ({
  options: { value, editorCallback },
  disabled,
  additionalStyles,
}) => {
  return (
    <Dropdown
      style={{
        width: "calc(100% - 0.5rem)",
        position: "inherit",
        ...additionalStyles,
      }}
      options={SIC_OPTIONS}
      value={value}
      placeholder="Select SIC Code"
      onChange={(e) => editorCallback?.(e.value)}
      disabled={disabled}
      filter
    />
  );
};

export const ConstructionCodeEditor: Editor = ({
  options: { value, editorCallback },
  disabled,
  additionalStyles,
}) => {
  return (
    <Dropdown
      style={{
        width: "calc(100% - 0.5rem)",
        position: "inherit",
        ...additionalStyles,
      }}
      options={ISO_CONSTRUCTION_OPTIONS}
      value={value}
      placeholder="Select Construction Code"
      onChange={(e) => editorCallback?.(e.value)}
      disabled={disabled}
      filter
    />
  );
};
